import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { IMG } from "@/lib/images";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

export const Vision = () => {
  const { t } = useLang();
  const titleLines = t.vision.title.split("\n");
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  // Image moves slightly slower than the page — classic parallax
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "10%"]);
  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1.04, 1.1]);

  return (
    <section
      id="vision"
      ref={sectionRef}
      data-testid="vision-section"
      className="relative py-28 md:py-40 bg-ivory text-forest"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center"
        >
          {/* Left: text */}
          <div className="lg:col-span-6">
            <motion.p variants={fadeUp} className="overline text-bronze mb-8" data-testid="vision-eyebrow">
              {t.vision.eyebrow}
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-light tracking-tight"
              data-testid="vision-title"
            >
              {titleLines.map((l, i) => (
                <span key={i} className="block">
                  {l}
                </span>
              ))}
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mt-10 text-base md:text-lg leading-relaxed text-forest/80 max-w-xl"
              data-testid="vision-body-1"
            >
              {t.vision.body_1}
            </motion.p>
            <motion.p
              variants={fadeUp}
              className="mt-6 text-base md:text-lg leading-relaxed text-forest/80 max-w-xl"
              data-testid="vision-body-2"
            >
              {t.vision.body_2}
            </motion.p>

            {/* Stats */}
            <motion.div
              variants={fadeUp}
              className="mt-14 grid grid-cols-3 gap-6 max-w-xl border-t border-forest/15 pt-10"
              data-testid="vision-stats"
            >
              {[
                { v: t.vision.stat_a_value, l: t.vision.stat_a_label },
                { v: t.vision.stat_b_value, l: t.vision.stat_b_label },
                { v: t.vision.stat_c_value, l: t.vision.stat_c_label },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-serif text-3xl md:text-4xl text-forest font-light">
                    {s.v}
                  </div>
                  <div className="overline text-forest/55 mt-2">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: image with scroll parallax */}
          <motion.div
            variants={fadeUp}
            className="lg:col-span-6 lg:col-start-8 relative"
          >
            <div className="aspect-[4/5] w-full overflow-hidden">
              <motion.img
                src={IMG.vision}
                alt="Cabin by a lake in pine forest"
                style={{ y: imgY, scale: imgScale }}
                className="w-full h-full object-cover will-change-transform"
                data-testid="vision-image-parallax"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block">
              <div className="bg-forest text-ivory px-6 py-5 max-w-[260px]">
                <p className="overline text-bronze mb-2">Friluftsliv</p>
                <p className="font-serif text-lg leading-snug">
                  The Norwegian art of being outdoors.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
